import { FC, useCallback, useState } from 'react'

import {
  GeneratedSite, CreditPack, SitePriceVariant, formatEuros, transcribeVoiceNote,
} from '@api/generated-site'
import { useSiteOffers, useRequestSiteEdit, useBuySiteCredits } from '@hooks/queries/generated-site'
import VoiceNoteButton from './VoiceNoteButton'

type Props = {
  site: GeneratedSite
  // grille de l'A/B test de prix du site (les packs de crédits sont identiques dans les deux grilles)
  priceVariant: SitePriceVariant
  onClose: () => void
  onOpenOffer: () => void
  onRequested: () => void
}

const EXAMPLES = [
  'Mets en avant mes tarifs à partir de 80 € et ajoute une page Galerie',
  'Change les couleurs pour quelque chose de plus chaleureux, et parle de mes 10 ans d’expérience',
  'Ajoute une section avis clients et un bouton pour prendre rendez-vous',
]

const PackButton: FC<{ pack: CreditPack, onBuy: () => void, pending: boolean }> = ({ pack, onBuy, pending }) => (
  <button
    type="button"
    onClick={onBuy}
    disabled={pending}
    className="flex flex-col items-start rounded-2xl border-2 border-black/10 px-4 py-3 text-left hover:border-black disabled:opacity-50"
  >
    <span className="font-aeonik text-lg font-medium">{pack.credits} crédits</span>
    <span className="text-xs text-black/60">{formatEuros(pack.amountHt)} HT · {formatEuros(pack.unitPriceHt)} le crédit</span>
  </button>
)

// panneau « Modifier mon site » : dictée ou texte, coût en crédits, achat de packs sur la carte enregistrée
const EditPanel: FC<Props> = ({
  site, priceVariant, onClose, onOpenOffer, onRequested,
}) => {
  const offers = useSiteOffers(priceVariant)
  const requestEdit = useRequestSiteEdit()
  const buyCredits = useBuySiteCredits()
  const [instruction, setInstruction] = useState('')
  const [inputMode, setInputMode] = useState<'voice' | 'text'>('text')
  const [exampleIndex, setExampleIndex] = useState(0)

  const isFree = site.freeEditsRemaining > 0
  const canAfford = isFree || site.creditsBalance >= site.editCost
  const needsPurchase = !site.purchasedAt && !isFree
  const valid = instruction.trim().length >= 5

  const onTranscribed = useCallback(async (audio: Blob) => {
    const result = await transcribeVoiceNote(audio, null)
    const transcript = (result.text || '').trim()
    if (!transcript) throw new Error('empty transcription')
    setInstruction((current) => (current.trim() ? `${current.trim()} ${transcript}` : transcript))
    setInputMode('voice')
  }, [])

  const errorCode = (requestEdit.error as { response?: { data?: { code?: string } } })?.response?.data?.code
  const errorMessage = requestEdit.isError
    ? ({
      site_busy: 'Une mise à jour est déjà en cours, patientez une minute.',
      no_credits: 'Plus assez de crédits : ajoutez un pack ci-dessous.',
      not_purchased: 'Activez votre site pour continuer à le modifier.',
      generation_failed: 'La mise à jour a échoué, vos crédits ont été recrédités. Réessayez.',
    } as Record<string, string>)[errorCode || ''] || 'Une erreur est survenue, réessayez.'
    : null

  const onSubmit = async () => {
    if (!valid || !canAfford) return
    try {
      await requestEdit.mutateAsync({ instruction: instruction.trim(), inputMode })
      onRequested()
    } catch (e) {
      // message affiché via errorMessage
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6">
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full md:max-w-[600px] max-h-[94vh] overflow-y-auto rounded-t-3xl md:rounded-3xl bg-white p-6 md:p-8 shadow-2xl animate-fade-up">
        <button type="button" onClick={onClose} aria-label="Fermer" className="absolute right-4 top-4 h-9 w-9 rounded-full bg-black/5 text-lg leading-none hover:bg-black/10">×</button>

        <div className="text-[11px] uppercase tracking-[0.2em] text-black/50 mb-2">Modifier mon site</div>
        <h2 className="font-aeonik text-2xl md:text-[28px] font-medium leading-tight mb-2">
          Dites ce que vous voulez changer. On s’occupe du reste.
        </h2>
        <p className="text-sm text-black/60 mb-5">
          Parlez ou écrivez comme à un webmaster : textes, pages, couleurs, photos, services. Votre site est régénéré en une à deux minutes.
        </p>

        <div className="flex items-center justify-between rounded-2xl bg-black text-white px-4 py-3 mb-4 text-sm">
          <span>
            {isFree
              ? <><strong>1 modification offerte</strong> pour essayer</>
              : <>Cette modification : <strong>{site.editCost} crédits</strong></>}
          </span>
          <span className="text-white/70">Solde : <strong className="text-white">{site.creditsBalance}</strong> crédits</span>
        </div>

        <div className="mb-3"><VoiceNoteButton onTranscribed={onTranscribed} /></div>

        <textarea
          value={instruction}
          onChange={(event) => { setInstruction(event.target.value); if (inputMode !== 'voice') setInputMode('text') }}
          rows={4}
          maxLength={1500}
          placeholder="Exemple : ajoute une page Tarifs avec mes trois formules, et un ton plus chaleureux"
          className="w-full rounded-2xl border border-black/15 bg-white text-sm md:text-base leading-relaxed focus:border-black focus:outline-none [&&&]:h-auto [&&&]:min-h-[120px] [&&&]:p-4 [&&&]:mb-0 resize-y"
        />
        <div className="mt-1 flex justify-between text-xs text-black/50">
          <button type="button" className="underline" onClick={() => { setInstruction(EXAMPLES[exampleIndex % EXAMPLES.length]); setExampleIndex((i) => i + 1) }}>
            Voir un exemple
          </button>
          <span>{instruction.trim().length} caractères</span>
        </div>

        {errorMessage && <div className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{errorMessage}</div>}

        {needsPurchase ? (
          <button type="button" onClick={onOpenOffer} className="mt-5 w-full rounded-2xl bg-black py-4 font-aeonik text-base font-medium text-white hover:bg-black/90">
            Activer mon site pour continuer à le modifier
          </button>
        ) : (
          <button
            type="button"
            onClick={onSubmit}
            disabled={!valid || !canAfford || requestEdit.isPending}
            className="mt-5 w-full rounded-2xl bg-black py-4 font-aeonik text-base font-medium text-white hover:bg-black/90 disabled:opacity-50"
          >
            {requestEdit.isPending ? 'Envoi…' : isFree ? 'Appliquer ma modification offerte' : `Appliquer · ${site.editCost} crédits`}
          </button>
        )}

        {site.purchasedAt && (
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-black/50 mb-2">Recharger mes crédits</div>
            <div className="grid grid-cols-3 gap-2">
              {(offers.data?.packs || []).map((pack) => (
                <PackButton key={pack.key} pack={pack} pending={buyCredits.isPending} onBuy={() => buyCredits.mutate(pack.key)} />
              ))}
            </div>
            {buyCredits.isError && <div className="mt-2 text-xs text-red-700">Le paiement du pack a été refusé.</div>}
            {buyCredits.isSuccess && <div className="mt-2 text-xs text-green-700">Crédits ajoutés, payés avec votre carte enregistrée.</div>}
          </div>
        )}

        {site.edits.length > 0 && (
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-black/50 mb-2">Vos dernières modifications</div>
            <ul className="space-y-2">
              {site.edits.slice(0, 4).map((edit) => (
                <li key={edit.id} className="flex items-start justify-between gap-3 text-xs text-black/70">
                  <span className="line-clamp-2">{edit.instruction}</span>
                  <span className="shrink-0 text-black/50">
                    {edit.status === 'DONE' ? 'appliquée' : edit.status === 'FAILED' ? 'échouée' : 'en cours'}
                    {edit.creditsCost > 0 ? ` · ${edit.creditsCost} cr.` : ' · offerte'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

export default EditPanel
